/**
 * Page publique d'un défi live privé — /defi-live/<code> (migration 0650).
 *
 * Sans session : présentation (organisateur, niveau, inscrits) puis au choix
 * « Jouer en invité » (prénom → session ANONYME, classé, rien de durable) ou
 * « Me connecter » (OTP sur place, retour automatique ici). Avec une session :
 * le player Live School du défi (salon d'attente, jeu, classement).
 */

import { useState } from 'react';
import { ArrowRight, UserRound } from 'lucide-react';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import {
  AvatarCircle,
  Button,
  CardDescription,
  CardTitle,
  CompassLogo,
  useTurnstile,
} from '@jeraisonne/ui';
import { OtpAuthForm } from '@/components/otp-auth-form';
import { useAuth } from '@/lib/auth';
import { setReturnTo } from '@/lib/return-to';
import { LiveQuizScreen } from '@/features/live-quiz/live-quiz-screen';
import {
  ensureGuestSession,
  isPrivateLiveFull,
  normalizeGuestName,
  playersCapLabel,
  normalizeJoinCode,
  privateLiveStage,
  privateLiveSummary,
  recallGuestName,
  rememberGuestName,
  usePrivateLive,
  type PrivateLive,
} from '@/lib/private-live';
import { liveCoverUrl } from '@/lib/live-covers';
import { ArenaPanel, LiveArena } from '@/features/live-ui/live-arena';
import { GlassChip, LiveBadge, liveCtaClass } from '@/features/live-ui/live-cover-card';
import { levelSubjectChip } from '@/features/live-ui/live-card-model';
import { PresenceCounts } from './private-lobby';

export function levelLabel(level: string): string {
  return SCHOOL_LEVELS.find((l) => l.value === level)?.label ?? level;
}

export function subjectLabel(subject: string | null): string | null {
  if (!subject) return null;
  return SUBJECTS.find((s) => s.value === subject)?.label ?? subject;
}

export function PrivateLivePage({ code: rawCode }: { code: string }) {
  const code = normalizeJoinCode(rawCode);
  const { session, loading } = useAuth();
  const userId = session?.user.id ?? null;
  const isAnonymous = session?.user.is_anonymous === true;
  const liveQuery = usePrivateLive(code, userId);
  const [guestName, setGuestName] = useState<string | null>(() => recallGuestName() || null);

  if (!code) {
    return (
      <Shell>
        <CardTitle>Code inconnu</CardTitle>
        <CardDescription>Vérifie le lien du défi.</CardDescription>
      </Shell>
    );
  }
  if (loading || liveQuery.isLoading) return <Shell>Chargement du défi…</Shell>;

  const live = liveQuery.data;
  if (!live) {
    return (
      <Shell>
        <CardTitle>Ce défi n’existe pas</CardTitle>
        <CardDescription>Il a peut-être été annulé par son organisateur.</CardDescription>
        <button
          type="button"
          className={liveCtaClass('glass', 'mt-4')}
          onClick={() => (window.location.href = '/')}
        >
          Accueil
        </button>
      </Shell>
    );
  }

  const stage = privateLiveStage(live, live.server_time);
  if (stage === 'finished' && !live.joined && !live.is_host) {
    return (
      <Shell live={live}>
        <DefiHeader live={live} />
        <CardDescription className="mt-3">Ce défi est terminé.</CardDescription>
      </Shell>
    );
  }

  if (isPrivateLiveFull(live)) {
    return (
      <Shell live={live}>
        <DefiHeader live={live} />
        <CardDescription className="mt-3">
          Ce défi est complet : {live.max_players} joueurs maximum. Demande à l’organisateur de
          créer un autre défi.
        </CardDescription>
      </Shell>
    );
  }

  if (!session) {
    return <JoinChoice live={live} code={code} onGuest={setGuestName} />;
  }

  return (
    <LiveQuizScreen
      niveau={live.level}
      userId={userId!}
      challengeId={live.id}
      privateLive={{ code, guestName: isAnonymous ? guestName : null, isGuest: isAnonymous }}
    />
  );
}

function DefiHeader({ live }: { live: PrivateLive }) {
  const hostName = live.host?.display_name ?? 'un joueur';
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <div className="flex flex-wrap items-center justify-center gap-2">
        <LiveBadge kind="private" />
        <GlassChip>{levelSubjectChip(live.level, live.subject)}</GlassChip>
      </div>
      <h1 className="font-display text-[1.75rem] font-extrabold leading-tight text-white">
        {live.title ?? 'Défi privé'}
      </h1>
      {live.host && (
        <div className="flex items-center gap-2">
          <AvatarCircle
            name={hostName}
            src={live.host.avatar_url}
            size="sm"
            className="ring-2 ring-[#f4c64e]"
          />
          <span className="text-ink-700 text-sm">
            Organisé par <strong className="text-white">{hostName}</strong>
          </span>
        </div>
      )}
      <p className="text-ink-600 text-sm">{privateLiveSummary(live.settings, live.step_count)}</p>
      <PresenceCounts
        registered={live.registered}
        connected={live.connected}
        cap={playersCapLabel(live)}
      />
    </div>
  );
}

function JoinChoice({
  live,
  code,
  onGuest,
}: {
  live: PrivateLive;
  code: string;
  onGuest: (name: string) => void;
}) {
  const [mode, setMode] = useState<'choice' | 'guest' | 'login'>('choice');
  const [name, setName] = useState(() => recallGuestName());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const turnstile = useTurnstile();

  async function playAsGuest() {
    const clean = normalizeGuestName(name);
    if (!clean) {
      setError('Écris ton prénom.');
      return;
    }
    setBusy(true);
    setError(null);
    const res = await ensureGuestSession(clean, () => turnstile.getToken());
    setBusy(false);
    if ('error' in res) {
      setError('Impossible de te connecter en invité. Réessaie.');
      return;
    }
    rememberGuestName(clean);
    onGuest(clean);
  }

  if (mode === 'login') {
    return (
      <Page live={live}>
        <OtpAuthForm
          initialMode="signup"
          renderHeader={({ step, email }) =>
            step === 'otp' ? (
              <>
                <CardTitle>Vérifie ta boîte mail</CardTitle>
                <CardDescription>
                  Entre le code à 6 chiffres envoyé à{' '}
                  <strong className="text-ink-700">{email}</strong>.
                </CardDescription>
              </>
            ) : (
              <DefiHeader live={live} />
            )
          }
        />
        <Button variant="ghost" fullWidth className="mt-3" onClick={() => setMode('choice')}>
          Retour
        </Button>
      </Page>
    );
  }

  return (
    <Shell live={live}>
      <DefiHeader live={live} />
      {mode === 'choice' ? (
        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            className={liveCtaClass('gold')}
            onClick={() => {
              setReturnTo(`/defi-live/${code}`);
              setMode('login');
            }}
          >
            Me connecter
            <ArrowRight className="h-5 w-5" aria-hidden />
          </button>
          <p className="text-ink-500 text-xs">
            Avec un compte, tes bonnes réponses te rapportent des XP et des HORA.
          </p>
          <button type="button" className={liveCtaClass('glass')} onClick={() => setMode('guest')}>
            <UserRound className="h-5 w-5" aria-hidden />
            Jouer en invité
          </button>
        </div>
      ) : (
        <form
          className="mt-5 space-y-3 text-left"
          onSubmit={(e) => {
            e.preventDefault();
            void playAsGuest();
          }}
        >
          <label htmlFor="guest-name" className="text-ink-700 block text-sm font-semibold">
            Ton prénom
          </label>
          <input
            id="guest-name"
            value={name}
            maxLength={30}
            autoComplete="given-name"
            onChange={(e) => setName(e.target.value)}
            className="border-ink-200 bg-ink-50 text-ink-900 h-11 w-full rounded-xl border px-3 text-sm"
          />
          <p className="text-ink-500 text-xs">
            En invité, tu es classé avec les autres, mais tu ne gardes ni XP ni HORA.
          </p>
          {error && (
            <p role="alert" className="text-sm text-[#fca5a5]">
              {error}
            </p>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <button
              type="button"
              className={liveCtaClass('glass', 'min-h-11 text-sm')}
              onClick={() => setMode('choice')}
            >
              Retour
            </button>
            <button type="submit" className={liveCtaClass('gold')} disabled={busy}>
              {busy ? 'Connexion…' : 'Rejoindre le défi'}
              {!busy && <ArrowRight className="h-5 w-5" aria-hidden />}
            </button>
          </div>
          <div ref={turnstile.containerRef} className="flex justify-center empty:hidden" />
        </form>
      )}
    </Shell>
  );
}

function Page({ live, children }: { live?: PrivateLive | null; children: React.ReactNode }) {
  const coverUrl = live ? liveCoverUrl({ path: live.cover_path, seed: live.id }) : null;
  return (
    <LiveArena coverUrl={coverUrl} tone="medium">
      <div className="native:pt-[env(safe-area-inset-top)]">
        <header className="flex justify-center px-6 py-6">
          <CompassLogo size="md" wordmarkClassName="text-white" />
        </header>
        <main className="mx-auto flex w-full max-w-md flex-col px-4 pb-[max(4rem,env(safe-area-inset-bottom))]">
          {children}
        </main>
      </div>
    </LiveArena>
  );
}

function Shell({ live, children }: { live?: PrivateLive | null; children: React.ReactNode }) {
  return (
    <Page live={live}>
      <ArenaPanel className="space-y-3 py-6 text-center sm:p-6">{children}</ArenaPanel>
    </Page>
  );
}
